import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import type { Moment } from 'moment';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { useDialog } from '@/components/dialogs/useDialog';
import type { CorrectionRecord, CorrectionDialogResult, CorrectionsFilter } from '@/types/monitoring';
import { useCorrectionsQuery } from './api';
import { CorrectionDetailsModal } from './CorrectionDetailsModal';
import { getProviderIconSrc } from './providerIcon';

const DEFAULT_FILTER: CorrectionsFilter = { nbCorrections: 30, startDate: null, endDate: null };

function rowBg(status: string): string {
  const s = status.toLowerCase();
  if (s === 'rejected') return 'error.light';
  if (s === 'pending') return 'warning.light';
  return 'inherit';
}

export function CorrectionsMonitoringPage() {
  const { t } = useTranslation();
  const { open: openDialog } = useDialog();

  // Form state
  const [nbCorrections, setNbCorrections] = useState(30);
  const [startDate, setStartDate] = useState<Moment | null>(null);
  const [endDate, setEndDate] = useState<Moment | null>(null);

  // Committed filter drives the query
  const [committedFilter, setCommittedFilter] = useState<CorrectionsFilter>(DEFAULT_FILTER);

  const isFilterValid =
    (startDate === null && endDate === null) ||
    (startDate !== null && endDate !== null && !startDate.isAfter(endDate));

  const query = useCorrectionsQuery(committedFilter);

  const allRows: CorrectionRecord[] = useMemo(() => query.data ?? [], [query.data]);

  // Client-side text filter
  const [textFilter, setTextFilter] = useState('');
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;

  const filtered = useMemo(() => {
    if (!textFilter.trim()) return allRows;
    const term = textFilter.toLowerCase();
    return allRows.filter(
      (c) =>
        (c.correctionType && c.correctionType.toLowerCase().includes(term)) ||
        (c.providerAccountId && c.providerAccountId.toLowerCase().includes(term)) ||
        (c.transactionType && c.transactionType.toLowerCase().includes(term)) ||
        (c.username && c.username.toLowerCase().includes(term)) ||
        (c.ticketId && c.ticketId.toString().includes(term)) ||
        (c.amount && c.amount.toString().includes(term)),
    );
  }, [allRows, textFilter]);

  const paged = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleFetch = () => {
    setTextFilter('');
    setPage(0);
    setCommittedFilter({
      nbCorrections,
      startDate: startDate ? startDate.toISOString() : null,
      endDate: endDate ? endDate.toISOString() : null,
    });
  };

  const handleReset = () => {
    setNbCorrections(30);
    setStartDate(null);
    setEndDate(null);
    setTextFilter('');
    setPage(0);
    setCommittedFilter({ ...DEFAULT_FILTER });
  };

  const handleRowClick = async (correction: CorrectionRecord) => {
    const result = await openDialog<CorrectionDialogResult | null>((close) => (
      <CorrectionDetailsModal correction={correction} onClose={close} />
    ));
    if (!result) return;
    if (result.code === 0) {
      const msg =
        result.type === 'reject'
          ? t('pages.monitoring.corrections.correction_rejected_successfully', {
              defaultValue: 'Correction rejected successfully.',
            })
          : t('pages.monitoring.corrections.correction_validated_successfully', {
              defaultValue: 'Correction validated successfully.',
            });
      await Swal.fire(
        t('pages.monitoring.corrections.success', { defaultValue: 'Success' }),
        msg,
        'success',
      );
      handleReset();
    } else {
      const errMsg =
        result.type === 'reject'
          ? t('pages.monitoring.corrections.correction_rejected_failed', {
              defaultValue: 'Failed to reject correction.',
            })
          : t('pages.monitoring.corrections.correction_validated_failed', {
              defaultValue: 'Failed to validate correction.',
            });
      await Swal.fire(t('messages.error', { defaultValue: 'Error' }), errMsg, 'error');
    }
  };

  const statusChipColor = (status: string): 'warning' | 'error' | 'success' | 'default' => {
    const s = status.toLowerCase();
    if (s === 'pending') return 'warning';
    if (s === 'rejected') return 'error';
    if (s === 'validated') return 'success';
    return 'default';
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {t('menus.monitoring_corrections', { defaultValue: 'Corrections Monitoring' })}
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'flex-end' }}
          flexWrap="wrap"
        >
          <TextField
            label={t('pages.monitoring.corrections.nb_corrections', {
              defaultValue: 'Number of corrections',
            })}
            type="number"
            size="small"
            value={nbCorrections}
            onChange={(e) => setNbCorrections(Number(e.target.value))}
            sx={{ minWidth: 180 }}
          />
          <DateTimePicker
            label={t('pages.monitoring.corrections.start_date', { defaultValue: 'Start date' })}
            value={startDate}
            onChange={(val) => setStartDate(val)}
            slotProps={{ textField: { size: 'small' } }}
          />
          <DateTimePicker
            label={t('pages.monitoring.corrections.end_date', { defaultValue: 'End date' })}
            value={endDate}
            onChange={(val) => setEndDate(val)}
            slotProps={{ textField: { size: 'small' } }}
          />
          <Button variant="contained" disabled={!isFilterValid} onClick={handleFetch}>
            {t('pages.monitoring.corrections.fetch', { defaultValue: 'Fetch' })}
          </Button>
          <Button variant="contained" color="error" onClick={handleReset}>
            {t('pages.monitoring.corrections.reset', { defaultValue: 'Reset' })}
          </Button>
        </Stack>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <TextField
          size="small"
          value={textFilter}
          onChange={(e) => {
            setTextFilter(e.target.value);
            setPage(0);
          }}
          placeholder={t('pages.user-list.filter', { defaultValue: 'Filter…' })}
          sx={{ mb: 2, minWidth: 280 }}
        />

        {query.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {t('pages.monitoring.corrections.table.correction_date', {
                        defaultValue: 'Date',
                      })}
                    </TableCell>
                    <TableCell>
                      {t('pages.monitoring.corrections.table.correction_type', {
                        defaultValue: 'Correction Type',
                      })}
                    </TableCell>
                    <TableCell>
                      {t('pages.monitoring.corrections.table.phone_number', {
                        defaultValue: 'Phone',
                      })}
                    </TableCell>
                    <TableCell>
                      {t('pages.monitoring.corrections.table.amount', { defaultValue: 'Amount' })}
                    </TableCell>
                    <TableCell>
                      {t('pages.monitoring.corrections.table.transaction_type', {
                        defaultValue: 'Transaction Type',
                      })}
                    </TableCell>
                    <TableCell>
                      {t('pages.monitoring.corrections.table.transfer_type', {
                        defaultValue: 'Transfer Type',
                      })}
                    </TableCell>
                    <TableCell>
                      {t('pages.monitoring.corrections.table.user', { defaultValue: 'User' })}
                    </TableCell>
                    <TableCell>
                      {t('pages.monitoring.corrections.table.ticket_id', {
                        defaultValue: 'Ticket ID',
                      })}
                    </TableCell>
                    <TableCell>
                      {t('pages.monitoring.corrections.table.status', { defaultValue: 'Status' })}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paged.map((row) => {
                    const iconSrc = getProviderIconSrc(row.providerIdentifier, row.transferType);
                    return (
                      <TableRow
                        key={row.id}
                        hover
                        sx={{ bgcolor: rowBg(row.status), cursor: 'pointer' }}
                        onClick={() => void handleRowClick(row)}
                      >
                        <TableCell>
                          {iconSrc && (
                            <Box
                              component="img"
                              src={iconSrc}
                              alt="provider"
                              sx={{ width: 20, height: 20, mr: 0.5, verticalAlign: 'middle' }}
                            />
                          )}
                          {new Date(row.correctionDate).toLocaleString('fr-FR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </TableCell>
                        <TableCell>
                          {t(
                            `pages.monitoring.corrections.correction_types.${row.correctionType.toLowerCase()}`,
                            { defaultValue: row.correctionType },
                          )}
                        </TableCell>
                        <TableCell>{row.providerAccountId}</TableCell>
                        <TableCell>{row.amount}</TableCell>
                        <TableCell>
                          {t(
                            `pages.monitoring.corrections.transaction_types.${row.transactionType.toLowerCase()}`,
                            { defaultValue: row.transactionType },
                          )}
                        </TableCell>
                        <TableCell>
                          {row.transactionType === 'TRANSFER'
                            ? t(
                                `pages.monitoring.transfers.${row.transferType.toLowerCase()}`,
                                { defaultValue: row.transferType },
                              )
                            : t('pages.monitoring.payments.payment', {
                                defaultValue: 'Payment',
                              })}
                        </TableCell>
                        <TableCell>{row.username}</TableCell>
                        <TableCell>{row.ticketId ?? '—'}</TableCell>
                        <TableCell>
                          <Chip
                            label={t(
                              `pages.monitoring.corrections.statuses.${row.status.toLowerCase()}`,
                              { defaultValue: row.status },
                            )}
                            color={statusChipColor(row.status)}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {paged.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                          {t('pages.monitoring.corrections.no_corrections_found', {
                            defaultValue: 'No corrections found.',
                          })}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[rowsPerPage]}
            />
          </>
        )}
      </Paper>
    </Box>
  );
}
